<template>
  <div class="host-overview">
    <section class="host-overview__intro">
      <article class="host-story-card">
        <span class="host-story-card__eyebrow">Resumen</span>
        <h2>{{ formatMoney(sales) }}</h2>
        <p>{{ storyLead }}</p>

        <div class="host-story-card__pills">
          <span><i class="fas fa-ticket-alt"></i>{{ tickets }} vendidos</span>
          <span><i class="fas fa-gift"></i>{{ courtesies }} cortesias</span>
          <span><i class="fas fa-layer-group"></i>{{ eventTickets.length }} etapas</span>
        </div>
      </article>

      <article class="host-story-card host-story-card--compact">
        <span class="host-story-card__eyebrow">Etapa lider</span>
        <strong>{{ topSellingTicket.name }}</strong>
        <p>{{ topSellingTicket.copy }}</p>
        <div class="host-story-card__footer">{{ topSellingTicket.metric }}</div>
      </article>
    </section>

    <section class="host-overview__metrics">
      <article v-for="cardItem in metricCards" :key="cardItem.label" class="host-stat-card">
        <small>{{ cardItem.label }}</small>
        <strong>{{ cardItem.value }}</strong>
        <span>{{ cardItem.caption }}</span>
      </article>
    </section>

    <section class="host-overview__grid">
      <article class="host-panel host-panel--chart">
        <div class="host-panel__header">
          <div>
            <span class="host-panel__eyebrow">Cobros</span>
            <h3>Como entra el dinero</h3>
          </div>
        </div>
        <div class="host-panel__chart-shell host-panel__chart-shell--bar">
          <Bar :data="paymentMixData" :options="barOptions" />
        </div>
      </article>

      <article class="host-panel host-panel--split">
        <div class="host-panel__header">
          <div>
            <span class="host-panel__eyebrow">Origen</span>
            <h3>De donde viene la compra</h3>
          </div>
        </div>

        <div class="host-traffic">
          <div class="host-panel__chart-shell host-panel__chart-shell--doughnut">
            <Doughnut :data="trafficChartData" :options="doughnutOptions" />
          </div>

          <div class="host-traffic__list">
            <article v-for="row in trafficRows" :key="row.label" class="host-traffic__item">
              <div>
                <strong>{{ row.label }}</strong>
                <span>{{ row.note }}</span>
              </div>
              <b>{{ row.value }}</b>
            </article>
          </div>
        </div>
      </article>
    </section>

    <section class="host-panel host-panel--daily">
      <div class="host-panel__header host-panel__header--daily">
        <div>
          <span class="host-panel__eyebrow">Ventas diarias</span>
          <h3>Avance diario de ventas</h3>
          <p>Revisa el comportamiento diario del evento o sigue el acumulado.</p>
        </div>

        <div class="host-tabs">
          <button :class="['host-tabs__button', { 'is-active': dailyMode === 'cumulative' }]" type="button" @click="dailyMode = 'cumulative'">Acumulado</button>
          <button :class="['host-tabs__button', { 'is-active': dailyMode === 'daily' }]" type="button" @click="dailyMode = 'daily'">Por dia</button>
        </div>
      </div>

      <div v-if="loadingDaily" class="host-empty">Cargando avance diario...</div>
      <div v-else-if="!dailyItems.length" class="host-empty">Todavia no hay ventas o pendientes para mostrar aqui.</div>
      <div v-else class="host-daily">
        <div class="host-panel__chart-shell host-panel__chart-shell--trend">
          <component :is="dailyChartComponent" :data="dailyChartData" :options="dailyChartOptions" />
        </div>

        <aside class="host-daily__aside">
          <article class="host-daily__stat">
            <small>Tickets aprobados</small>
            <strong>{{ dailySummary.approved_tickets || 0 }}</strong>
            <span>{{ formatMoney(dailySummary.approved_revenue) }} en ventas aprobadas</span>
          </article>
          <article class="host-daily__stat">
            <small>Mejor dia</small>
            <strong>{{ peakDayLabel }}</strong>
            <span>{{ peakDayValue }}</span>
          </article>
          <article class="host-daily__stat">
            <small>Ultimo movimiento</small>
            <strong>{{ latestDayLabel }}</strong>
            <span>{{ latestDayValue }}</span>
          </article>
        </aside>
      </div>

      <div v-if="dailyItems.length" class="host-table-wrap">
        <table class="host-table">
          <thead>
            <tr>
              <th>Dia</th>
              <th>Vendidos</th>
              <th>Cortesias</th>
              <th>Pendientes</th>
              <th>Revenue</th>
              <th>{{ dailyMode === 'cumulative' ? 'Acumulado' : 'Avg ticket' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in dailyItems" :key="row.date">
              <td>{{ formatDateLabel(row.date) }}</td>
              <td>{{ row.sold }}</td>
              <td>{{ row.courtesies }}</td>
              <td>{{ row.pending }}</td>
              <td>{{ formatMoney(row.revenue) }}</td>
              <td>{{ dailyMode === 'cumulative' ? row.cumulative_sold : formatMoney(row.average_ticket) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="host-panel">
      <div class="host-panel__header host-panel__header--daily">
        <div>
          <span class="host-panel__eyebrow">Inventario</span>
          <h3>Lectura operativa del inventario</h3>
        </div>

        <div class="host-tabs">
          <button :class="['host-tabs__button', { 'is-active': reportTab === 'Ventas x Código' }]" type="button" @click="reportTab = 'Ventas x Código'">Ventas x Código</button>
          <button :class="['host-tabs__button', { 'is-active': reportTab === 'Ventas x Sección' }]" type="button" @click="reportTab = 'Ventas x Sección'">Ventas x Sección</button>
        </div>
      </div>

      <div class="host-table-wrap" v-if="reportTab === 'Ventas x Código'">
        <table class="host-table">
          <thead>
            <tr>
              <th>Codigo / etapa</th>
              <th>Emitidas</th>
              <th>Restantes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in total_tickets" :key="ticket.id">
              <td>{{ ticket.sect }}</td>
              <td>{{ ticket.tkt }}</td>
              <td>{{ ticket.rem }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="loadingSections" class="host-empty">Cargando ventas por seccion...</div>
      <div v-else class="host-table-wrap">
        <table class="host-table">
          <thead>
            <tr>
              <th>Seccion</th>
              <th>Entradas</th>
              <th>Validado</th>
              <th>Pendiente</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="section in sectionSales" :key="section.id">
              <td>{{ section.name }}</td>
              <td>{{ section.sales.Sales }}</td>
              <td>{{ formatMoney(section.sales.Subtotal) }}</td>
              <td>{{ formatMoney(section.sales.Pending) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import { Bar, Doughnut, Line } from 'vue-chartjs'
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Filler, Legend, LineElement, LinearScale, PointElement, Title, Tooltip } from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement, LineElement, PointElement, Filler)

export default {
  name: 'TestView',

  components: {
    Bar,
    Doughnut,
    Line,
  },

  props: {
    tickets: { required: true },
    sales: { required: true },
    courtesies: { required: true },
    eventTickets: { required: true },
    generatedData: { required: true },
    generatedData2: { required: true },
    total_tickets: { required: true },
    card: { required: true },
    cardPos: { required: true },
    bank: { required: true },
    cash: { required: true },
    event: { required: true },
  },

  data() {
    return {
      reportTab: 'Ventas x Código',
      dailyMode: 'cumulative',
      sectionSales: [],
      loadingSections: false,
      dailyItems: [],
      dailySummary: {},
      loadingDaily: false,
    }
  },

  computed: {
    storyLead() {
      if ((this.dailySummary && this.dailySummary.peak_day) || this.tickets > 0) {
        const peak = this.dailySummary && this.dailySummary.peak_day
        if (peak) {
          return `Tu mejor dia fue ${this.formatDateLabel(peak.date)} con ${peak.sold} tickets y ${this.formatMoney(peak.revenue)} en ventas.`
        }

        return `El evento ya suma ${this.tickets} tickets aprobados y ${this.formatMoney(this.sales)} en ventas.`
      }

      return 'Todavia no hay ventas aprobadas, pero aqui veras el movimiento apenas entren las primeras ordenes.'
    },

    topSellingTicket() {
      if (!Array.isArray(this.total_tickets) || this.total_tickets.length === 0) {
        return {
          name: 'Aun sin etapa lider',
          copy: 'Cuando entren ventas, aqui veras la etapa que mejor responde.',
          metric: 'Esperando actividad',
        }
      }

      const winner = [...this.total_tickets].sort((left, right) => Number(right.tkt || 0) - Number(left.tkt || 0))[0]
      return {
        name: winner.sect,
        copy: `${winner.rem} restantes disponibles en esta etapa.`,
        metric: `${winner.tkt} emitidas`,
      }
    },

    metricCards() {
      return [
        {
          label: 'Venta aprobada',
          value: this.formatMoney(this.sales),
          caption: 'Ventas aprobadas del evento',
        },
        {
          label: 'Entradas vendidas',
          value: this.tickets,
          caption: 'Tickets aprobados en total',
        },
        {
          label: 'Cortesias emitidas',
          value: this.courtesies,
          caption: 'Invitaciones y accesos de cortesia',
        },
        {
          label: 'Pagos pendientes',
          value: this.dailySummary.pending_tickets || 0,
          caption: 'Tickets aun por validar',
        },
      ]
    },

    paymentMixData() {
      return {
        labels: ['Tarjeta', 'Transferencia', 'Efectivo', 'POS'],
        datasets: [
          {
            borderRadius: 16,
            backgroundColor: ['#8fe2dc', '#79aee4', '#f0c17a', '#d7e4ff'],
            data: [this.card, this.bank, this.cash, this.cardPos].map((value) => Number.parseFloat(value || 0)),
          },
        ],
      }
    },

    trafficChartData() {
      return this.generatedData2 || { labels: [], datasets: [{ data: [] }] }
    },

    trafficRows() {
      const dataset = (this.generatedData2 && this.generatedData2.datasets && this.generatedData2.datasets[0]) || { data: [] }
      const values = dataset.data || []
      return [
        { label: 'Meta / IG', value: values[0] || 0, note: 'FB e Instagram' },
        { label: 'TikTok', value: values[1] || 0, note: 'Contenido de video corto' },
        { label: 'Mail', value: values[2] || 0, note: 'Base y newsletters' },
        { label: 'Promotores', value: values[3] || 0, note: 'Embajadores y hosts' },
        { label: 'Sitio web', value: values[4] || 0, note: 'Venta organica o directa' },
      ]
    },

    dailyChartComponent() {
      return this.dailyMode === 'cumulative' ? Line : Bar
    },

    dailyChartData() {
      const labels = this.dailyItems.map((item) => this.formatShortDate(item.date))

      if (this.dailyMode === 'cumulative') {
        return {
          labels,
          datasets: [
            {
              label: 'Tickets acumulados',
              data: this.dailyItems.map((item) => item.cumulative_sold),
              borderColor: '#8fe2dc',
              backgroundColor: 'rgba(143, 226, 220, 0.16)',
              fill: true,
              tension: 0.34,
            },
          ],
        }
      }

      return {
        labels,
        datasets: [
          {
            label: 'Tickets por dia',
            data: this.dailyItems.map((item) => item.sold),
            backgroundColor: '#8bbce2',
            borderRadius: 16,
            borderSkipped: false,
          },
        ],
      }
    },

    dailyChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              color: '#d8e1f4',
            },
          },
        },
        scales: {
          x: {
            ticks: { color: 'rgba(210, 220, 238, 0.72)' },
            grid: { color: 'rgba(255, 255, 255, 0.04)' },
          },
          y: {
            ticks: { color: 'rgba(210, 220, 238, 0.72)', precision: 0 },
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
          },
        },
      }
    },

    doughnutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: '#d8e1f4',
              padding: 18,
            },
          },
        },
      }
    },

    barOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            ticks: { color: 'rgba(210, 220, 238, 0.72)' },
            grid: { display: false },
          },
          y: {
            ticks: { color: 'rgba(210, 220, 238, 0.72)' },
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
          },
        },
      }
    },

    peakDayLabel() {
      const peakDay = this.dailySummary.peak_day
      return peakDay ? this.formatDateLabel(peakDay.date) : 'Sin pico'
    },

    peakDayValue() {
      const peakDay = this.dailySummary.peak_day
      return peakDay ? `${peakDay.sold} tickets · ${this.formatMoney(peakDay.revenue)}` : 'Todavia sin ventas aprobadas'
    },

    latestDayLabel() {
      const latestDay = this.dailySummary.latest_day
      return latestDay ? this.formatDateLabel(latestDay.date) : 'Sin movimiento'
    },

    latestDayValue() {
      const latestDay = this.dailySummary.latest_day
      return latestDay ? `${latestDay.sold} tickets · ${latestDay.pending} pendientes` : 'Aun no hay registros'
    },
  },

  created() {
    this.loadDailySales()
  },

  watch: {
    reportTab(value) {
      if (value === 'Ventas x Sección' && this.sectionSales.length === 0) {
        this.loadSectionSales()
      }
    },
  },

  methods: {
    loadSectionSales() {
      this.loadingSections = true
      apiService(`/api/venue-section-report/?event=${this.event.id}`).then((data) => {
        this.sectionSales = Array.isArray(data) ? data : []
      }).finally(() => {
        this.loadingSections = false
      })
    },

    loadDailySales() {
      this.loadingDaily = true
      apiService(`/api/host/events/${this.event.id}/daily-sales/`).then((data) => {
        this.dailyItems = data && Array.isArray(data.items) ? data.items : []
        this.dailySummary = data && data.summary ? data.summary : {}
      }).finally(() => {
        this.loadingDaily = false
      })
    },

    formatMoney(value) {
      const parsed = Number.parseFloat(value || 0)
      return `$${Number.isNaN(parsed) ? '0.00' : parsed.toFixed(2)}`
    },

    formatDateLabel(value) {
      if (!value) {
        return 'Sin fecha'
      }

      return new Intl.DateTimeFormat('es-EC', { dateStyle: 'medium' }).format(new Date(`${value}T00:00:00`))
    },

    formatShortDate(value) {
      if (!value) {
        return '--'
      }

      return new Intl.DateTimeFormat('es-EC', { month: 'short', day: 'numeric' }).format(new Date(`${value}T00:00:00`))
    },
  },
}
</script>

<style scoped>
.host-overview {
  display: grid;
  gap: 20px;
}

.host-overview__intro {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
  gap: 16px;
}

.host-story-card,
.host-story-card--compact {
  position: relative;
  overflow: hidden;
  display: grid;
  gap: 12px;
  padding: 22px;
  border-radius: 30px;
  background:
    radial-gradient(circle at 14% 0%, rgba(208, 218, 236, 0.1), transparent 24%),
    radial-gradient(circle at 84% 14%, rgba(92, 71, 130, 0.16), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.008)),
    rgba(7, 8, 13, 0.96);
  border: 1px solid rgba(196, 206, 228, 0.12);
  box-shadow: 0 26px 58px rgba(0, 0, 0, 0.3);
}

.host-story-card__eyebrow {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.host-story-card h2,
.host-story-card--compact strong {
  margin: 0;
  color: #f4f7ff;
  font-family: 'Bodoni Moda', serif;
}

.host-story-card h2 {
  font-size: clamp(2.4rem, 3.8vw, 4rem);
  line-height: 0.9;
}

.host-story-card p,
.host-story-card--compact p,
.host-story-card__footer {
  margin: 0;
  color: rgba(204, 214, 232, 0.82);
  line-height: 1.6;
}

.host-story-card__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.host-story-card__pills span,
.host-story-card__footer {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(196, 206, 228, 0.1);
}

.host-story-card--compact strong {
  font-size: clamp(1.5rem, 2.1vw, 2.2rem);
  line-height: 0.96;
}

.host-overview__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.host-stat-card,
.host-panel {
  position: relative;
  overflow: hidden;
  border-radius: 28px;
  background:
    radial-gradient(circle at 14% 0%, rgba(208, 218, 236, 0.08), transparent 26%),
    radial-gradient(circle at 84% 14%, rgba(92, 71, 130, 0.14), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.008)),
    rgba(7, 8, 13, 0.96);
  border: 1px solid rgba(196, 206, 228, 0.12);
  box-shadow: 0 24px 56px rgba(0, 0, 0, 0.28);
}

.host-stat-card {
  display: grid;
  gap: 8px;
  padding: 18px;
}

.host-stat-card small,
.host-panel__eyebrow,
.host-daily__stat small {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.host-stat-card strong,
.host-daily__stat strong {
  color: #f4f7ff;
  font-size: clamp(1.6rem, 2.2vw, 2.4rem);
  line-height: 0.96;
}

.host-stat-card span,
.host-daily__stat span,
.host-panel__header p {
  color: rgba(198, 207, 226, 0.8);
  line-height: 1.55;
}

.host-overview__grid,
.host-daily {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  gap: 18px;
}

.host-panel {
  display: grid;
  gap: 16px;
  padding: 20px;
}

.host-panel__header,
.host-panel__header--daily {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.host-panel__header h3 {
  margin: 4px 0 0;
  color: #f4f7ff;
  font-size: clamp(1.4rem, 2vw, 2rem);
}

.host-panel__header p {
  margin: 8px 0 0;
  max-width: 46ch;
}

.host-panel__chart-shell {
  position: relative;
  min-height: 320px;
}

.host-panel__chart-shell--doughnut {
  min-height: 280px;
}

.host-panel__chart-shell--trend {
  min-height: 360px;
}

.host-traffic {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(260px, 1.1fr);
  gap: 18px;
  align-items: center;
}

.host-traffic__list,
.host-daily__aside {
  display: grid;
  gap: 10px;
}

.host-traffic__item,
.host-daily__stat {
  display: grid;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(196, 206, 228, 0.08);
}

.host-traffic__item strong {
  color: #f0f4fd;
}

.host-traffic__item span {
  color: rgba(198, 207, 226, 0.72);
  font-size: 0.86rem;
}

.host-traffic__item b {
  color: #8fe2dc;
  font-size: 1.3rem;
}

.host-tabs {
  display: inline-flex;
  gap: 8px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(196, 206, 228, 0.08);
}

.host-tabs__button {
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: rgba(209, 220, 238, 0.82);
  font-weight: 700;
}

.host-tabs__button.is-active {
  background: linear-gradient(135deg, #dfe8ff, #9ee5da);
  color: #081013;
}

.host-table-wrap {
  overflow: auto;
}

.host-table {
  width: 100%;
  border-collapse: collapse;
}

.host-table th,
.host-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(196, 206, 228, 0.08);
  text-align: left;
  white-space: nowrap;
}

.host-table th {
  color: #d5dff2;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.host-table td {
  color: rgba(222, 230, 244, 0.88);
}

.host-empty {
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  color: rgba(198, 207, 226, 0.8);
}

@media (max-width: 1100px) {
  .host-overview__intro,
  .host-overview__metrics,
  .host-overview__grid,
  .host-daily,
  .host-traffic {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .host-overview {
    gap: 14px;
  }

  .host-panel,
  .host-stat-card {
    padding: 16px;
    border-radius: 22px;
  }

  .host-panel__header,
  .host-panel__header--daily {
    display: grid;
  }

  .host-tabs {
    width: 100%;
    justify-content: space-between;
  }

  .host-tabs__button {
    flex: 1 1 0;
    padding: 0 10px;
    font-size: 0.84rem;
  }

  .host-panel__chart-shell,
  .host-panel__chart-shell--trend {
    min-height: 280px;
  }
}
</style>
